import { datadogRum } from '@datadog/browser-rum';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo } from 'react';
import authApi from '~/authApi';
import { FEATURE_FLAGS } from '~/constants';
import { usePersistedState } from '~/hooks/usePersistedState';
import { useUserSettings } from '~/hooks/useUserSettings.ts';

import { useFlagStore } from './store';

const FeatureFlagController = dynamic(
  () => import('./FeatureFlagsController'),
  { ssr: false },
);

const SHAREABLE_FLAG_ROUTE_PATTERNS: readonly RegExp[] = [
  /^\/shared\/t\//,
  /^\/shared\/media-kits\//,
];

const usesShareableFlagRoute = () =>
  typeof window !== 'undefined' &&
  SHAREABLE_FLAG_ROUTE_PATTERNS.some((pattern) =>
    pattern.test(window.location.pathname),
  );

export const FeatureFlags = ({
  maxRetries = 2,
}: {
  maxRetries?: number;
} = {}) => {
  const usesShareableFlags = usesShareableFlagRoute();

  const { updateFlags, setStatus, status } = useFlagStore();
  const [localStorageFF, setLocalStorageFF] = usePersistedState<
    FEATURE_FLAGS[]
  >(`featureFlags-${authApi.user?.id || 'guest-user'}`, []);

  useUserSettings({
    enabled: !usesShareableFlags,
    validate: (data) => {
      // Throw error if no enabled_features to trigger retry
      if (isEmpty(data.enabled_features)) {
        throw new Error('Empty feature flags response from API');
      }
    },
    retry: maxRetries,
    onRetryDelay: (retryAttempt) => {
      if (retryAttempt > 2 && status === 'loading') setStatus('error');
      return 300;
    },
    onSuccess: (data) => {
      refreshFeatureFlags(data.enabled_features);
    },
    onError: (error) => {
      if (error instanceof AxiosError && error.response) {
        refreshFeatureFlags();
        datadogRum.addError(error, {
          message: 'Error ocurred while retrieving feature flags',
          localStorageFallback: localStorageFF,
        });
      } else if (error instanceof Error) {
        // Handle validation errors (e.g., empty/null/undefined enabled_features)
        refreshFeatureFlags();
        datadogRum.addError(error, {
          message: error.message,
          localStorageFallback: localStorageFF,
        });
      }
    },
  });

  const refreshFeatureFlags = useCallback(
    (flags?: FEATURE_FLAGS[]) => {
      if ((!flags || flags.length === 0) && localStorageFF.length === 0) {
        setStatus('error');
        return;
      }
      flags && setLocalStorageFF(flags);
      setStatus('success');
    },
    [setLocalStorageFF, setStatus, localStorageFF],
  );

  const shouldShowFlagsUI = useMemo(() => {
    if (status === 'loading') return false;
    const isFeatureFlagControllerEnabled = localStorageFF?.includes(
      FEATURE_FLAGS.foamFeatureFlagController,
    );

    return isFeatureFlagControllerEnabled;
  }, [localStorageFF, status]);

  useEffect(() => {
    if (localStorageFF.length === 0) return;
    updateFlags(localStorageFF);
    Object.values(FEATURE_FLAGS).forEach((flag) =>
      datadogRum.addFeatureFlagEvaluation(flag, localStorageFF.includes(flag)),
    );
  }, [localStorageFF, updateFlags]);

  if (shouldShowFlagsUI) {
    return <FeatureFlagController featureFlags={localStorageFF} />;
  }

  return null;
};
